import React from 'react';
import _ from 'lodash';

import {
  BulkReceiveSpecificReturnAuthorizationsModal
} from 'modules';

import { Table } from 'iron-forge-react-modules';
import { Button, Skeleton } from '@mui/material';
import * as ReturnAuthorizationGroupNestedTable from './ReturnAuthorizationGroupNestedTable/ReturnAuthorizationGroupNestedTable';
import * as types from './types';
import * as dataService from './dataService';



const TableComponent = Table.genTableComponent<types.IColumnId>();



export interface IProps {
  filter: types.IFilter;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.filter);



  const handleClickReceive = React.useCallback(
    (group: dataService.IReturnAuthorizationGroup) => {
      BulkReceiveSpecificReturnAuthorizationsModal.open({
        lines: group.returnAuthorizations.map(rma => ({
          returnAuthorizationId: rma.id,
          serialNumber: rma.expectedSerialNumber,
          item: {
            id: rma.itemId,
            displayName: `${rma.itemName} ${rma.itemSku}`
          },
          store: {
            id: rma.storeId,
            displayName: rma.storeName
          }
        }))
      });
    },
    []
  );



  const columns = React.useMemo((): Table.IColumn<types.IColumnId>[] => [
    {
      id: 'preAlertDate',
      label: "Pre-Alert Date"
    },
    {
      id: 'clientName',
      label: "Client"
    },
    {
      id: 'referenceNumber',
      label: "Reference Number"
    },
    {
      id: 'totalReturnsSummary',
      label: "Received / Total"
    },
    {
      id: 'actions',
      label: ""
    }
  ], []);



  const loadingRows = useLoadingRows(columns);

  const loadedRows = React.useMemo(
    () => queryTuple.data.groups.map((group): Table.IRow<types.IColumnId> => ({
      id: group.id,
      columnId2Value: {
        preAlertDate: {
          type: 'string',
          label: group.createdTimestamp.toFormat('MM/dd/yyyy')
        },
        clientName: {
          type: 'string',
          label: group.storeName
        },
        referenceNumber: {
          type: 'string',
          label: group.referenceNumber
        },
        totalReturnsSummary: {
          type: 'string',
          label: `${group.totalReturns - group.returnAuthorizations.length} / ${group.totalReturns.toString()}`
        },
        actions: {
          type: 'component',
          sortableValue: '',
          label: (
            <Button
              size="small"
              onClick={_.partial(handleClickReceive, group)}>
              Receive
            </Button>
          )
        }
      },
      expandedContent: (
        <ReturnAuthorizationGroupNestedTable.FC
          returnAuthorizations={group.returnAuthorizations} />
      )
    })),
    [
      handleClickReceive,
      queryTuple.data.groups
    ]
  );

  return (
    <TableComponent
      rows={queryTuple.loading ? loadingRows : loadedRows}
      columns={columns} />
  );
};




export function useLoadingRows<TColumnId extends string>(columns: Table.IColumn<TColumnId>[]) {
  return React.useMemo(
    () => _.times(20).map((i): Table.IRow<TColumnId> => ({
      id: i.toString(),
      columnId2Value: _.mapValues(_.keyBy(columns, col => col.id), (): Table.IRowComponentValue => ({
        type: 'component',
        sortableValue: i,
        label: <Skeleton />
      })) as Record<TColumnId, Table.IRowComponentValue>
    })),
    [columns]
  );
}
